import React, { useState, useEffect, useMemo } from 'react';
import { SUPPORT_CATEGORIES, type SupportRequest, type SupportStatus } from '../types/support';
import {
  getLocalSupportRequests,
  updateSupportRequestStatus,
  deleteSupportRequest,
  syncSupportRequestsWithServer,
  onSupportRequestsChange,
} from '../utils/supportSync';

interface SupportRequestsTrackerModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: { name?: string; email?: string } | null;
  isAdminUser: boolean;
}

const STATUS_COLORS: Record<SupportStatus, { bg: string; text: string; border: string; icon: string }> = {
  Pending: {
    bg: 'bg-amber-100 dark:bg-amber-950/60',
    text: 'text-amber-800 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-700',
    icon: '⏳',
  },
  'In Progress': {
    bg: 'bg-blue-100 dark:bg-blue-950/60',
    text: 'text-blue-800 dark:text-blue-300',
    border: 'border-blue-300 dark:border-blue-700',
    icon: '🔄',
  },
  Resolved: {
    bg: 'bg-emerald-100 dark:bg-emerald-950/60',
    text: 'text-emerald-800 dark:text-emerald-300',
    border: 'border-emerald-300 dark:border-emerald-700',
    icon: '✓',
  },
  Closed: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-gray-300 dark:border-gray-700',
    icon: '🔒',
  },
};

export const SupportRequestsTrackerModal: React.FC<SupportRequestsTrackerModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  isAdminUser,
}) => {
  const [requests, setRequests] = useState<SupportRequest[]>(() => getLocalSupportRequests());
  const [activeTab, setActiveTab] = useState<'my' | 'all'>('my');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | SupportStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [notesInput, setNotesInput] = useState('');

  const userEmail = (currentUser?.email || '').toLowerCase().trim();

  // Sync on modal open and listen for updates
  useEffect(() => {
    if (isOpen) {
      setRequests(getLocalSupportRequests());
      setIsSyncing(true);
      syncSupportRequestsWithServer().finally(() => setIsSyncing(false));
    }

    const unsubscribe = onSupportRequestsChange((updated) => {
      setRequests(updated);
    });

    return unsubscribe;
  }, [isOpen]);

  // Filter requests based on active tab, category, status, and search query
  const filteredRequests = useMemo(() => {
    return requests.filter((req) => {
      // Tab filter
      if (activeTab === 'my') {
        if (req.userEmail.toLowerCase() !== userEmail) return false;
      }

      // Category filter
      if (categoryFilter !== 'all') {
        const reqCat = (req.issueCategory || req.assignedTo || '').toLowerCase();
        if (!reqCat.includes(categoryFilter.toLowerCase())) {
          return false;
        }
      }

      // Status filter
      if (statusFilter !== 'all' && req.status !== statusFilter) {
        return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchTitle = req.fileName.toLowerCase().includes(q);
        const matchUser = req.userName.toLowerCase().includes(q) || req.userEmail.toLowerCase().includes(q);
        const matchCategory = (req.issueCategory || req.assignedTo || '').toLowerCase().includes(q);
        const matchDesc = req.description.toLowerCase().includes(q);
        const matchNotes = (req.statusNotes || '').toLowerCase().includes(q);
        const matchColCategory = (req.fileCategory || '').toLowerCase().includes(q);
        if (!matchTitle && !matchUser && !matchCategory && !matchDesc && !matchNotes && !matchColCategory) {
          return false;
        }
      }

      return true;
    });
  }, [requests, activeTab, categoryFilter, statusFilter, searchQuery, userEmail]);

  // Counts for tabs
  const myCount = useMemo(
    () => requests.filter((r) => r.userEmail.toLowerCase() === userEmail).length,
    [requests, userEmail]
  );

  const handleStatusChange = async (reqId: string, newStatus: SupportStatus) => {
    await updateSupportRequestStatus(reqId, newStatus);
    setRequests(getLocalSupportRequests());
  };

  const handleSaveNotes = async (reqId: string) => {
    await updateSupportRequestStatus(reqId, requests.find((r) => r.id === reqId)?.status || 'Pending', notesInput);
    setEditingNotesId(null);
    setNotesInput('');
    setRequests(getLocalSupportRequests());
  };

  const handleDelete = async (reqId: string) => {
    if (confirm('Are you sure you want to delete this support request?')) {
      await deleteSupportRequest(reqId);
      setRequests(getLocalSupportRequests());
    }
  };

  const handleManualRefresh = async () => {
    setIsSyncing(true);
    await syncSupportRequestsWithServer();
    setRequests(getLocalSupportRequests());
    setIsSyncing(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 max-w-4xl w-full h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-fade-in text-gray-900 dark:text-gray-100">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50/80 dark:bg-gray-800/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-xl">
              ✋
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                Support Requests Tracker
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Track status and assistance requests submitted across intranet sheets and documents.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Direct Link to Google Sheet */}
            <a
              href="https://docs.google.com/spreadsheets/d/17i731owZg2uMhd_98isFdtfyQ4W2J_nIU-hwqZUovoE/edit"
              target="_blank"
              rel="noopener noreferrer"
              className="px-2.5 py-1.5 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer shadow-sm"
              title="Open the Google Sheet directly in Google Drive"
            >
              <span>📊</span>
              <span className="hidden sm:inline">Google Sheet</span>
            </a>

            <button
              onClick={handleManualRefresh}
              disabled={isSyncing}
              title="Refresh from server / Google Sheet"
              className="px-2.5 py-1.5 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <span className={isSyncing ? 'animate-spin' : ''}>🔄</span>
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-white text-lg font-bold p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Navigation Tabs & Filters */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 space-y-3 bg-white dark:bg-gray-900 flex-shrink-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* View Tabs */}
            <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800/80 p-1 rounded-xl border border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setActiveTab('my')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  activeTab === 'my'
                    ? 'bg-amber-500 text-gray-950 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <span>My Requests</span>
                <span className="bg-black/20 text-inherit text-[10px] px-1.5 py-0.2 rounded-full">
                  {myCount}
                </span>
              </button>

              <button
                onClick={() => setActiveTab('all')}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  activeTab === 'all'
                    ? 'bg-amber-500 text-gray-950 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <span>All Clinic Requests</span>
                <span className="bg-black/20 text-inherit text-[10px] px-1.5 py-0.2 rounded-full">
                  {requests.length}
                </span>
              </button>
            </div>

            {/* Category & Status Filter Controls */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Category Dropdown */}
              <div className="relative">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-2.5 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg text-xs font-semibold outline-none cursor-pointer appearance-none pr-7 shadow-sm"
                >
                  <option value="all">All Categories</option>
                  {SUPPORT_CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400 text-xs">
                  ▼
                </div>
              </div>

              {/* Status Filter Pills */}
              <div className="flex items-center gap-1 text-xs">
                {(['all', 'Pending', 'In Progress', 'Resolved', 'Closed'] as const).map((st) => (
                  <button
                    key={st}
                    onClick={() => setStatusFilter(st)}
                    className={`px-2.5 py-1 rounded-lg font-semibold transition-colors cursor-pointer ${
                      statusFilter === st
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {st === 'all' ? 'All Status' : st}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by file name, category, requester, notes, or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full text-xs bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl px-3 py-2 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 outline-none transition-all shadow-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-bold cursor-pointer"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Requests List */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3.5 bg-gray-50/50 dark:bg-gray-950/40">
          {filteredRequests.length === 0 ? (
            <div className="py-16 text-center text-gray-400 space-y-2">
              <div className="text-3xl">✋</div>
              <p className="text-sm font-semibold">No support requests found.</p>
              <p className="text-xs text-gray-500">
                {searchQuery || statusFilter !== 'all' || categoryFilter !== 'all'
                  ? 'Try adjusting your search, category, or status filters.'
                  : 'Click the raise hand (✋) icon next to any sheet or printable document to submit an assistance request.'}
              </p>
            </div>
          ) : (
            filteredRequests.map((req) => {
              const statusMeta = STATUS_COLORS[req.status] || STATUS_COLORS.Pending;
              const isOwner = req.userEmail.toLowerCase() === userEmail;
              const canEditStatus = isAdminUser || isOwner;
              const categoryName = req.issueCategory || req.assignedTo || 'General Help';

              return (
                <div
                  key={req.id}
                  className="bg-white dark:bg-gray-800/90 rounded-2xl border border-gray-200 dark:border-gray-700/80 p-4 sm:p-5 space-y-3.5 shadow-sm hover:border-amber-400/60 dark:hover:border-amber-500/40 transition-all"
                >
                  {/* Card Header */}
                  <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 dark:border-gray-700/60 pb-3">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono font-bold text-gray-400">
                          #{req.id.substring(req.id.length - 6).toUpperCase()}
                        </span>
                        {/* Issue Category Badge */}
                        <span className="text-[11px] font-bold bg-indigo-100 dark:bg-indigo-950/80 text-indigo-800 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60 px-2.5 py-0.5 rounded-full">
                          {categoryName}
                        </span>
                        <span className="text-[10px] uppercase font-bold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">
                          {req.sourceColumnLabel}
                        </span>
                        {req.fileCategory && (
                          <span className="text-[10px] font-bold bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                            {req.fileCategory}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          {new Date(req.createdAt).toLocaleDateString()} at{' '}
                          {new Date(req.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>

                      <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                        <a
                          href={req.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title="Open target document"
                        >
                          {req.fileName}
                        </a>
                        {req.fileUrl && (
                          <a
                            href={req.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-blue-500 text-xs"
                          >
                            🔗
                          </a>
                        )}
                      </h3>
                    </div>

                    {/* Status Display or Dropdown */}
                    <div className="flex items-center gap-2">
                      {canEditStatus ? (
                        <div className="relative">
                          <select
                            value={req.status}
                            onChange={(e) => handleStatusChange(req.id, e.target.value as SupportStatus)}
                            className={`px-3 py-1 rounded-xl text-xs font-bold border ${statusMeta.bg} ${statusMeta.text} ${statusMeta.border} cursor-pointer appearance-none pr-6 outline-none shadow-sm`}
                          >
                            <option value="Pending">⏳ Pending</option>
                            <option value="In Progress">🔄 In Progress</option>
                            <option value="Resolved">✓ Resolved</option>
                            <option value="Closed">🔒 Closed</option>
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-current text-xs">
                            ▼
                          </div>
                        </div>
                      ) : (
                        <span
                          className={`px-3 py-1 rounded-xl text-xs font-bold border flex items-center gap-1 ${statusMeta.bg} ${statusMeta.text} ${statusMeta.border}`}
                        >
                          <span>{statusMeta.icon}</span>
                          <span>{req.status}</span>
                        </span>
                      )}

                      {(isAdminUser || isOwner) && (
                        <button
                          onClick={() => handleDelete(req.id)}
                          title="Delete Request"
                          className="p-1 text-gray-400 hover:text-red-500 text-xs rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Requester Meta */}
                  <div className="text-xs bg-gray-50 dark:bg-gray-900/60 p-2.5 rounded-xl border border-gray-200/60 dark:border-gray-700/40 flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <span className="text-gray-400 text-[10px] uppercase font-bold mr-1.5">Submitted By:</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">{req.userName}</span>{' '}
                      <span className="text-gray-400 text-[11px]">({req.userEmail})</span>
                    </div>
                    {req.resolvedAt && (
                      <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                        ✓ Resolved on {new Date(req.resolvedAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="space-y-1">
                    <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                      Issue / Request Details:
                    </span>
                    <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-white dark:bg-gray-900/40 p-3 rounded-xl border border-gray-100 dark:border-gray-800">
                      {req.description}
                    </p>
                  </div>

                  {/* Resolution Notes */}
                  {(req.statusNotes || editingNotesId === req.id || canEditStatus) && (
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 space-y-1.5">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Resolution / Status Notes:
                        </span>
                        {canEditStatus && editingNotesId !== req.id && (
                          <button
                            onClick={() => {
                              setEditingNotesId(req.id);
                              setNotesInput(req.statusNotes || '');
                            }}
                            className="text-blue-600 dark:text-blue-400 hover:underline font-bold text-xs cursor-pointer"
                          >
                            {req.statusNotes ? 'Edit Notes' : '+ Add Notes'}
                          </button>
                        )}
                      </div>

                      {editingNotesId === req.id ? (
                        <div className="space-y-2">
                          <textarea
                            rows={2}
                            value={notesInput}
                            onChange={(e) => setNotesInput(e.target.value)}
                            placeholder="Add updates, resolution details, or notes..."
                            className="w-full text-xs p-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-amber-500"
                          />
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setEditingNotesId(null)}
                              className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-xs font-semibold rounded-lg text-gray-700 dark:text-gray-300"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleSaveNotes(req.id)}
                              className="px-3.5 py-1 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-500 shadow-sm"
                            >
                              Save Notes
                            </button>
                          </div>
                        </div>
                      ) : (
                        req.statusNotes && (
                          <p className="text-xs text-emerald-800 dark:text-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/30 p-2.5 rounded-xl border border-emerald-200 dark:border-emerald-800/40 italic">
                            "{req.statusNotes}"
                          </p>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
